import { useRef } from 'react';
import { arrayMove, useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { css } from '@emotion/react';
import { useTranslation } from '@frontend/i18n';
import { Icon } from '@frontend/icons';
import { theme } from '@frontend/theme';
import { Button, PopoverMenu, PopoverMenuItem, usePopoverMenu } from '@frontend/design-system';
import { useCalendarGridStore, useNonReactiveStore } from '../../store/calendar-grid.store';
import { CalendarColumnHeaderType, ColumnHeaderOverlayStyleFn } from '../../types';
import { CalendarDragStateContext, defaultCalendarDragState } from '../calendar-drag-state-context';
import { ColumnHeader } from './column-header';

interface SortableColumnHeaderProps {
  columnId: string;
  headerType: CalendarColumnHeaderType;
  /** The group this column belongs to — needed for Move Left/Right menu actions */
  groupId: string;
  /** Ordered list of all column IDs in this group — needed for Move Left/Right */
  columnIds: string[];
}

export const SortableColumnHeader = ({ columnId, headerType, groupId, columnIds }: SortableColumnHeaderProps) => {
  const { t } = useTranslation('calendar-grid');
  const dragStyles = useCalendarGridStore((s) => s.dragStyles);
  const nonReactiveStore = useNonReactiveStore();
  const popoverMenuProps = usePopoverMenu({ placement: 'bottom-end' });
  const { getTriggerProps, isOpen: isMenuOpen } = popoverMenuProps;
  const menuButtonContainerRef = useRef<HTMLDivElement>(null);

  const isColumnReorderDragging = useCalendarGridStore((s) => s.isColumnReorderDragging);
  const isColumnReorderActive = useCalendarGridStore((s) => s.isColumnReorderActive);
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: columnId });

  const columnHeaderOverlayStyle = isDragging
    ? typeof dragStyles.columnHeaderOverlay === 'function'
      ? (dragStyles.columnHeaderOverlay as ColumnHeaderOverlayStyleFn)(columnId)
      : dragStyles.columnHeaderOverlay
    : undefined;

  const columnIndex = columnIds.indexOf(columnId);
  const isFirst = columnIndex === 0;
  const isLast = columnIndex === columnIds.length - 1;

  const moveColumn = (direction: 'left' | 'right') => {
    const targetIndex = direction === 'left' ? columnIndex - 1 : columnIndex + 1;
    if (targetIndex < 0 || targetIndex >= columnIds.length) return;
    const newColumnIds = arrayMove(columnIds, columnIndex, targetIndex);
    nonReactiveStore.getState().onColumnReorder?.(groupId, newColumnIds);
  };

  // Prevent drag activation when clicking the menu button or its floating popover
  const onPointerDownCapture = (e: React.PointerEvent) => {
    const inMenu =
      menuButtonContainerRef.current?.contains(e.target as Node) ||
      e.target === popoverMenuProps.refs.floating.current ||
      popoverMenuProps.refs.floating.current?.contains(e.target as Node);
    if (inMenu) e.stopPropagation();
  };

  return (
    <div
      ref={setNodeRef}
      style={{ transform: CSS.Transform.toString(transform), transition }}
      css={[
        containerStyle,
        isDragging && sourceStyle,
        (isColumnReorderDragging || isColumnReorderActive) && reorderActiveStyle,
        isMenuOpen && menuOpenStyle,
      ]}
      data-dragging={isDragging ? 'true' : undefined}
      {...attributes}
      onPointerDownCapture={onPointerDownCapture}
    >
      {/* Drag handle — only this element has dnd-kit listeners */}
      <div
        css={dragHandleStyle}
        className='calendar-grid-column-drag-handle'
        {...listeners}
        onMouseDown={(e) => e.stopPropagation()}
      >
        <Icon name='dragand-drop-handle' size={20} color='subdued' />
      </div>

      {/* Column content */}
      <CalendarDragStateContext.Provider
        value={{ ...defaultCalendarDragState, isColumnHeaderOverlay: isDragging, columnHeaderOverlayStyle }}
      >
        <div css={contentStyle} className='calendar-grid-column-content'>
          <ColumnHeader columnId={columnId} headerType={headerType} groupId={groupId} />
        </div>
      </CalendarDragStateContext.Provider>

      {/* Menu button with Move Left / Move Right actions */}
      <div ref={menuButtonContainerRef} css={menuButtonContainerStyle} className='calendar-grid-column-menu-button'>
        <Button iconName='more' variant='secondary' size='small' {...getTriggerProps()} />
        <PopoverMenu {...popoverMenuProps.getMenuProps()}>
          <PopoverMenuItem
            {...popoverMenuProps.getItemProps({ index: 0 })}
            Icon={() => <Icon name='arrow-right-small' />}
            onClick={() => moveColumn('right')}
            disabled={isLast}
          >
            {t('Move to the Right')}
          </PopoverMenuItem>
          <PopoverMenuItem
            {...popoverMenuProps.getItemProps({ index: 1 })}
            Icon={() => <Icon name='back-small' />}
            onClick={() => moveColumn('left')}
            disabled={isFirst}
          >
            {t('Move to the Left')}
          </PopoverMenuItem>
        </PopoverMenu>
      </div>
    </div>
  );
};

const containerStyle = css({
  position: 'relative',
  display: 'flex',
  alignItems: 'center',
  overflow: 'hidden',
  padding: theme.spacing(0, 1),
  '&:hover': {
    '.calendar-grid-column-drag-handle': { display: 'flex', opacity: 1, cursor: 'grab' },
    '.calendar-grid-column-menu-button': { display: 'flex', opacity: 1 },
    '.calendar-grid-column-content': { padding: theme.spacing(0, 2.5) },
  },
});

const sourceStyle = css({ opacity: 0.2 });

const reorderActiveStyle = css({
  '&& .calendar-grid-column-drag-handle': { display: 'flex', opacity: 1, cursor: 'grab' },
  '&& .calendar-grid-column-content': { padding: theme.spacing(0, 2.5) },
});

const menuOpenStyle = css({
  '.calendar-grid-column-menu-button': { opacity: 1, display: 'flex' },
  '.calendar-grid-column-content': { padding: theme.spacing(0, 2.5) },
});

const dragHandleStyle = css({
  position: 'absolute',
  left: theme.spacing(0.5),
  top: '50%',
  transform: 'translateY(-50%)',
  display: 'none',
  opacity: 0,
  flexShrink: 0,
  cursor: 'grab',
  transition: 'opacity 0.2s ease-in-out',
  touchAction: 'none',
});

const contentStyle = css({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  minWidth: 0,
  flex: 1,
  transition: 'padding 0.2s ease-in-out',
});

const menuButtonContainerStyle = css({
  position: 'absolute',
  right: theme.spacing(0.5),
  top: '50%',
  transform: 'translateY(-50%)',
  display: 'none',
  opacity: 0,
  transition: 'opacity 0.2s ease-in-out',
  '> button': { padding: theme.spacing(0.25), height: 'auto' },
});
